import { CheckCircle, Info, WarningCircle } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { warningSurfaceTextStyle } from "../warningVisual";

export type AlertTone = "info" | "success" | "warning" | "danger";
export type AlertSize = "xs" | "sm" | "md" | "lg";

/* Exact compiled output of the pre-migration Tailwind utilities. The warning
 * tone keeps its inline-style treatment (warningVisual). */

const alertToneStyles: Record<AlertTone, CSSObject> = {
  info: {
    borderColor: "hsl(var(--bfrs-color-info) / .2)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-info-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-info-contrast) / var(--tw-text-opacity, 1))"
  },
  success: {
    borderColor: "hsl(var(--bfrs-color-success) / .2)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-success-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-success-contrast) / var(--tw-text-opacity, 1))"
  },
  warning: {},
  danger: {
    borderColor: "hsl(var(--bfrs-color-danger) / .2)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
  }
};

const alertSizeStyles: Record<AlertSize, CSSObject> = {
  xs: { gap: ".25rem", padding: ".375rem", fontSize: "10px" },
  sm: { gap: ".5rem", padding: ".625rem", fontSize: ".75rem", lineHeight: "1rem" },
  md: { gap: ".75rem", padding: "1rem", fontSize: ".875rem", lineHeight: "1.25rem" },
  lg: { gap: "1rem", padding: "1.25rem", fontSize: "1rem", lineHeight: "1.5rem" }
};

interface AlertOwnerState {
  tone: AlertTone;
  size: AlertSize;
  alignStart: boolean;
}

const AlertRoot = styled("div", {
  label: "AgenticAlert-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: AlertOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    borderRadius: "var(--bfrs-radius-xl)",
    borderWidth: "1px"
  },
  alertToneStyles[ownerState.tone],
  alertSizeStyles[ownerState.size],
  ownerState.alignStart ? { alignItems: "flex-start" } : { alignItems: "center" }
]);

const AlertIcon = styled("span", {
  label: "AgenticAlert-icon"
})({
  display: "flex",
  flexShrink: 0,
  alignItems: "center"
});

const AlertContent = styled("div", {
  label: "AgenticAlert-content"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const AlertTitle = styled("div", {
  label: "AgenticAlert-title",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { hasBody: boolean } }>(({ ownerState }) => [
  {
    fontWeight: 600,
    lineHeight: "1.25rem",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.hasBody && { marginBottom: ".25rem" }
]);

const AlertBody = styled("div", {
  label: "AgenticAlert-body"
})({
  lineHeight: "1.25rem"
});

const AlertAction = styled("div", {
  label: "AgenticAlert-action"
})({
  flexShrink: 0
});

const iconSizeMap = { xs: 10, sm: 14, md: 18, lg: 20 } as const;

const iconMap = {
  info: Info,
  success: CheckCircle,
  warning: Info,
  danger: WarningCircle
};

export type AlertTextPart = { text: string; bold?: boolean };

export type AlertProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  tone?: AlertTone | null;
  size?: AlertSize | null;
  title?: ReactNode;
  message?: ReactNode;
  description?: ReactNode;
  /** Structured inline text with mixed bold/normal segments. Takes precedence over message/description/children. */
  parts?: AlertTextPart[];
  action?: ReactNode;
  icon?: ReactNode;
};

function hasRenderableNode(node: ReactNode): boolean {
  if (Array.isArray(node)) return node.some(hasRenderableNode);
  return node !== undefined && node !== null && node !== false && node !== "";
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(({ className, tone, size = "md", title, message, description, parts, action, icon, children, style, ...props }, ref) => {
  const resolvedTone = tone ?? "info";
  const Icon = iconMap[resolvedTone];
  const warningStyle = resolvedTone === "warning" ? warningSurfaceTextStyle : undefined;
  const body = parts
    ? parts.map((part, i) => part.bold ? <strong key={i}>{part.text}</strong> : <span key={i}>{part.text}</span>)
    : (hasRenderableNode(children) ? children : (message ?? description));
  const hasTitle = hasRenderableNode(title);
  const hasBody = parts ? parts.length > 0 : hasRenderableNode(body);

  return (
    <AlertRoot
      ref={ref}
      role={resolvedTone === "danger" ? "alert" : "status"}
      ownerState={{ tone: resolvedTone, size: size ?? "md", alignStart: Boolean(hasTitle && hasBody) }}
      className={className}
      style={{ ...warningStyle, ...style }}
      {...props}
    >
      <AlertIcon>{icon ?? <Icon size={iconSizeMap[size ?? "md"]} weight="bold" />}</AlertIcon>
      <AlertContent>
        {hasTitle && <AlertTitle ownerState={{ hasBody }}>{title}</AlertTitle>}
        {hasBody && <AlertBody>{body}</AlertBody>}
      </AlertContent>
      {action && <AlertAction>{action}</AlertAction>}
    </AlertRoot>
  );
});

Alert.displayName = "Alert";
